import Card from '@mui/material/Card';
import {
  Box,
  Button,
  CardHeader,
  CardMedia,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  Link,
  Popover,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CardContent from '@mui/material/CardContent';
import React, { ChangeEvent, FC, ReactNode, useContext, useEffect, useState } from 'react';
import {
  Check,
  Clear,
  ExpandLess,
  ExpandMore,
  GppMaybe,
  Settings,
  Star,
  StarBorder,
  Verified,
} from '@mui/icons-material';
import styled from 'styled-components';
import { constants } from '../../constants/constants';
import { IGmbLocationOpeningTime } from '../../shared/interfaces/IGmbLocationOpeningTime';
import Status from '../Status/Status';
import { Trans, useTranslation } from 'react-i18next';
import GMBService from '../../services/GMBService';
import CommunityManagementConfigurator from '../ReviewMonitoringGuide/CommunityManagementConfigurator';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { AuthContext } from '../../providers/AuthProvider';
import { customColors } from '../../constants/theme';
import { Cross } from 'recharts';

interface Props {
  ID: string;
  locationId: string;
  companyName: string;
  addressLines?: string[];
  zip: string;
  city: string;
  image?: string;
  times: IGmbLocationOpeningTime[];
  status: string;
  verificationStatus: string;
  phoneNumber: string;
  isActive: boolean;
  allowReviewResponses: boolean;
  communityManagement: CommunityManagement;
}

const CardContainer = styled.div<{ $selected: boolean }>`
  background-color: ${(props) =>
    props.$selected
      ? `rgba(${customColors.primary.main}, 0.1)`
      : props.theme.palette.background.paper};
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const LocationCard: FC<Props> = ({
  ID,
  locationId,
  companyName,
  addressLines,
  zip,
  city,
  image,
  times,
  status,
  verificationStatus,
  phoneNumber,
  isActive,
  allowReviewResponses,
  communityManagement,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  const [selected, setSelected] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [timesExpanded, setTimesExpanded] = useState(false);
  const [locationActive, setLocationActive] = useState(isActive);
  const [locationCommenting, setLocationCommenting] = useState(allowReviewResponses);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [communityManagementSettings, setCommunityManagementSettings] =
    useState<CommunityManagement>(communityManagement);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleChange = () => {
    setSelected((current) => !current);
  };

  const saveCommunityManagementSettings = async () => {
    await GMBService.saveCommunityManagementSettings(ID, communityManagementSettings, setLoading);
  };

  const statusIcon = {
    VERIFIED: (
      <Verified
        fontSize="small"
        color="disabled"
      />
    ),
    WARNING: (
      <GppMaybe
        fontSize="small"
        sx={{ color: theme.palette.error.dark }}
      />
    ),
  }[verificationStatus] as ReactNode;

  useEffect(() => {
    GMBService.toggleLocationActive(ID, locationActive);
  }, [ID, locationActive]);

  return (
    <Card
      sx={{
        boxShadow: 'unset',
        border: `1px solid 
      ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: theme.spacing(1),
      }}
    >
      <CardContainer $selected={selected}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                key={locationId}
                onChange={(ev) => setLocationActive(ev.target.checked)}
                checked={locationActive}
              />
              <Checkbox
                disabled
                icon={<StarBorder sx={{ color: theme.palette.warning.main }} />}
                checkedIcon={<Star sx={{ color: theme.palette.warning.main }} />}
                sx={{ display: 'none', ml: theme.spacing(1) }}
              />
              {locationActive ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.success.main,
                      fontWeight: 'bold',
                      ml: 'auto',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setLocationActive((active) => !active);
                    }}
                  >
                    <Check /> <Typography variant={'subtitle2'}>{t('enabled')}</Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.error.main,
                      fontWeight: 'bold',
                      ml: 'auto',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setLocationActive((active) => !active);
                    }}
                  >
                    <Clear /> <Typography variant={'subtitle2'}>{t('disabled')}</Typography>
                  </Box>
                </>
              )}
              <Tooltip
                title={t('location_settings')}
                arrow
              >
                <IconButton onClick={handleClick}>
                  <Settings />
                </IconButton>
              </Tooltip>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                sx={{ '& .MuiPaper-root': { minWidth: 400, px: 2, py: 2 } }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700 }}
                >
                  {t('settings')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(2),
                  }}
                >
                  <Typography variant="body1">{t('location') + ' ' + t('active')}</Typography>
                  <Tooltip
                    title={t('location_switch_tooltip')}
                    placement="top"
                    arrow
                  >
                    <Switch
                      checked={locationActive}
                      onChange={async (event: ChangeEvent<HTMLInputElement>, checked) => {
                        setLocationActive(checked);
                      }}
                    />
                  </Tooltip>
                </Box>
                {hasRole('can-use-ai') && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body1">{t('allow_commenting')}</Typography>
                      <Tooltip
                        title={t('allow_commenting_tooltip')}
                        arrow
                      >
                        <Switch
                          checked={locationCommenting}
                          onChange={async (event: ChangeEvent<HTMLInputElement>, checked) => {
                            setLocationCommenting(checked);
                            await GMBService.toggleLocationCommenting(ID, checked);
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Divider sx={{ mt: theme.spacing(2) }} />
                    <Box>
                      <CommunityManagementConfigurator
                        settings={communityManagementSettings}
                        onChange={setCommunityManagementSettings}
                      />
                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={async () => await saveCommunityManagementSettings()}
                        sx={{ mt: theme.spacing(2) }}
                      >
                        {t('save')}
                      </Button>
                    </Box>
                  </>
                )}
              </Popover>
            </Box>
          }
          sx={{ p: theme.spacing(1) }}
        />
        <CardMedia
          component="img"
          height="140"
          image={image ?? constants.gmbDefaultImage}
          alt=""
        />
        <CardContent
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            p: theme.spacing(2),
            '&:last-child': { p: theme.spacing(2) },
          }}
        >
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography variant="h6">{companyName}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 'auto',
                color: theme.palette.text.disabled,
              }}
            >
              <Status
                text={verificationStatus.toLowerCase()}
                iconBefore={statusIcon}
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: timesExpanded ? 0 : theme.spacing(1),
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: theme.palette.success.main }}
              >
                <Trans t={t}>{status}</Trans>
              </Typography>
              {times !== null && !timesExpanded && (
                <ExpandMore
                  fontSize="small"
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    setTimesExpanded((prevState) => {
                      return !prevState;
                    })
                  }
                />
              )}
              {times !== null && timesExpanded && (
                <ExpandLess
                  fontSize="small"
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    setTimesExpanded((prevState) => {
                      return !prevState;
                    })
                  }
                />
              )}
            </Box>
            {times !== null && (
              <Collapse
                in={timesExpanded}
                timeout="auto"
                unmountOnExit
                sx={{ my: timesExpanded ? theme.spacing(1) : 0 }}
              >
                {times.map((openingTime, index) => {
                  return (
                    <Box
                      key={index}
                      sx={{ display: 'flex' }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mr: theme.spacing(1), fontWeight: 700 }}
                      >
                        <Trans t={t}>{openingTime.openDay}</Trans>:
                      </Typography>
                      <Typography variant="body2">
                        {openingTime.openTime.hours} - {openingTime.closeTime.hours}
                      </Typography>
                    </Box>
                  );
                })}
              </Collapse>
            )}
          </Box>
          {addressLines !== undefined && addressLines.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                color: theme.palette.text.secondary,
              }}
            >
              {addressLines.map((streetPart, index) => {
                return (
                  <Typography
                    key={index}
                    variant="body2"
                  >
                    {streetPart}
                  </Typography>
                );
              })}
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            {zip} {city}
          </Typography>
          {phoneNumber && (
            <Box sx={{ display: 'flex' }}>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Tel.:
              </Typography>
              <Link
                href={`tel:${phoneNumber}`}
                variant="body2"
                underline="none"
                sx={{
                  color: theme.palette.info.main,
                  ml: theme.spacing(0.5),
                  mb: theme.spacing(2),
                }}
              >
                {phoneNumber}
              </Link>
            </Box>
          )}
          {hasRole('can-edit') && (
            <Box sx={{ mt: 'auto' }}>
              <Button
                disabled
                variant="outlined"
                size="small"
                sx={{ mr: theme.spacing(1) }}
              >
                {t('manage_location')}
              </Button>
              <Button
                disabled
                variant="outlined"
                size="small"
              >
                {t('verify')}
              </Button>
            </Box>
          )}
        </CardContent>
      </CardContainer>
    </Card>
  );
};

export default LocationCard;
